import { useEffect, useState } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import helpers from 'workflow/utils/helpers'

import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import {
  hideRightPanel,
  selectRightPanelVisibility,
  showRightPanel,
} from 'features/unifiedFlow/store/unifiedFlowSlice'

import CampaignLogs from '../../components/CampaignLogs'
import RightPanel from '../../components/layout/RightPanel'
import { useSelectedElement } from '../../hooks'

import CollapsedIcons from './CollapsedIcons'
import ExpandedIcons from './ExpandedIcons'

export type ActivePanel = 'logs'
export type HandleActivePanelChange = (panel: ActivePanel) => void

type Props = {
  onInputResultsClick: () => void
}

const RightSection = ({ onInputResultsClick }: Props) => {
  const { showUfManualResults } = useFlags()

  const dispatch = useAppDispatch()
  const [activePanel, setActivePanel] =
    useState<ActivePanel | undefined>(undefined)

  const { data: selectedElement } = useSelectedElement()
  const rightPanelVisibility = useAppSelector(selectRightPanelVisibility)

  const campaign = useAppSelector((state) => state.campaignStates.campaignData)

  const isBandit = helpers.isBanditCampaign(campaign.campaign_configuration)
  const showInputResultsButton =
    showUfManualResults &&
    (!isBandit ||
      helpers.isBanditManualConversionsCampaign(
        campaign.campaign_configuration
      )) &&
    campaign?.campaign_progress?.text_variants_accepted

  const handleActivePanelChange: HandleActivePanelChange = (
    panel: ActivePanel
  ) => {
    if (panel === activePanel) {
      setActivePanel(undefined)
      dispatch(hideRightPanel())
    } else {
      setActivePanel(panel)
      if (rightPanelVisibility !== 'expanded') {
        dispatch(showRightPanel())
      }
    }
  }

  useEffect(() => {
    setActivePanel(undefined)
    if (!selectedElement) {
      dispatch(hideRightPanel({ visibility: 'none' }))
    } else {
      dispatch(hideRightPanel({ visibility: 'collapsed' }))
    }
  }, [dispatch, selectedElement])

  return (
    <RightPanel
      collapsedIcons={
        <CollapsedIcons
          showInputResultsButton={showInputResultsButton}
          onInputResultsClick={onInputResultsClick}
          handleActivePanelChange={handleActivePanelChange}
        />
      }
      onOutsideClick={() => {
        setActivePanel(undefined)
        dispatch(hideRightPanel())
      }}
    >
      <CampaignLogs
        expandedIcons={
          <ExpandedIcons
            activePanel={activePanel}
            showInputResultsButton={showInputResultsButton}
            onInputResultsClick={onInputResultsClick}
            handleActivePanelChange={handleActivePanelChange}
          />
        }
      />
    </RightPanel>
  )
}

export default RightSection
